import React, {useEffect, useRef, useState} from 'react';
import {
  View,
  Text,
  StyleSheet,
  Dimensions,
  TouchableOpacity,
  ScrollView,
  Animated,
  Easing,
  Platform
} from 'react-native';
import Svg, {
  Rect,
  Text as SvgText,
  LinearGradient,
  Stop,
  Defs,
  G
} from 'react-native-svg';
import {useDispatch, useSelector} from 'react-redux';
import {getStatisticsItemsPerHourDetails} from '../../../actions/statisticsAction';
import {
  ChartLoading,
  NoDataDisplay,
  GridLines,
  YAxisLabels
} from './ChartComponents';
import {
  processItemsPerHourStatistics,
  generateYAxisValues,
  calculateDailyChartDimensions,
  calculateOptimalDisplayTime,
  getOptimalFontSize,
  truncateLabel
} from './chartUtils';
import AutoScrollWrapper from './AutoScrollWrapper';
import usePolling from './usePolling';
import useChartAnimations from './useChartAnimations';
import {ChartContent, ChartBackground} from './svgComponents';

const AnimatedRect = Animated.createAnimatedComponent(Rect);
const AnimatedSvgText = Animated.createAnimatedComponent(SvgText);
const isWeb = Platform.OS === 'web';

const getNameDisplaySettings = stackHeight => {
  const valueFontSize = getOptimalFontSize(stackHeight);
  const minHeightForName = 80;
  const showName = stackHeight >= minHeightForName && valueFontSize > 0;
  const nameFontSize = showName ? Math.max(valueFontSize - 2, 10) : 0;
  return {
    showName,
    nameFontSize,
    valueFontSize
  };
};

const ItemsPerHourChart = ({updateAutoTime, autoScrollEnabled = true, metricType = 'items'}) => {
  const dispatch = useDispatch();
  const response = useSelector(state => state.statistics.itemsPerHourList);

  const [containerDimensions, setContainerDimensions] = useState({
    width: Dimensions.get('window').width * 0.9,
    height: 450
  });

  const [seriesByPacker, setSeriesByPacker] = useState({all: []});
  const [displayData, setDisplayData] = useState([]);
  const [packerOptions, setPackerOptions] = useState([]);
  const [selectedPacker, setSelectedPacker] = useState('all');
  const [maxValue, setMaxValue] = useState(0);
  const [loading, setLoading] = useState(!response?.data?.statistics);
  const [barWidth, setBarWidth] = useState(80);
  const [spacing, setSpacing] = useState(20);
  const [chartWidth, setChartWidth] = useState(0);
  const [shouldScroll, setShouldScroll] = useState(false);

  const [stackedAnimations, setStackedAnimations] = useState({});
  const hasAnimatedRef = useRef(false);
  const animations = useChartAnimations(displayData, 800, 300, 100);
  const updateAutoTimeRef = useRef(updateAutoTime);
  const lastAutoTimeRef = useRef(null);

  useEffect(() => {
    updateAutoTimeRef.current = updateAutoTime;
  }, [updateAutoTime]);

  const maxBarsToShow = 6;
  const barGapRatio = 0.2;
  const yAxisWidth = 50;
  const segmentsCount = 6;

  const onContainerLayout = event => {
    const {width, height} = event.nativeEvent.layout;
    setContainerDimensions({width, height});
  };

  usePolling(() => {
    dispatch(getStatisticsItemsPerHourDetails());
  }, 5000);

  useEffect(() => {
    if (!response?.data) {
      setLoading(false);
      return;
    }

    const result = processItemsPerHourStatistics(response, metricType);
    if (!result.success) {
      setSeriesByPacker({all: []});
      setDisplayData([]);
      setPackerOptions([]);
      setMaxValue(0);
      setLoading(false);
      return;
    }

    setSeriesByPacker(result.seriesByPacker || {all: []});
    setPackerOptions(result.packers || []);
    setMaxValue(result.maxValue || 0);
    setLoading(false);
  }, [response?.data?.statistics, metricType]);

  useEffect(() => {
    if (selectedPacker === 'all') {
      return;
    }

    if (!seriesByPacker[selectedPacker]) {
      setSelectedPacker('all');
    }
  }, [seriesByPacker, selectedPacker]);

  useEffect(() => {
    const selectedSeries = seriesByPacker[selectedPacker] || [];
    setDisplayData(selectedSeries);

    if (selectedSeries.length) {
      const maxVal = Math.max(...selectedSeries.map(item => {
        if (item.isStacked && item.total) {
          return item.total;
        }
        return item.value || 0;
      }));
      setMaxValue(Math.ceil(maxVal * 1.1));
    } else {
      setMaxValue(0);
    }
  }, [seriesByPacker, selectedPacker]);

  useEffect(() => {
    const isStacked = selectedPacker === 'all' && displayData.length > 0 && displayData[0]?.isStacked;
    if (isStacked) {
      hasAnimatedRef.current = false;
      const newAnimations = {};
      displayData.forEach((item, index) => {
        newAnimations[`bar-${index}`] = new Animated.Value(0);
        if (item.stacks) {
          item.stacks.forEach((_, stackIndex) => {
            newAnimations[`bar-${index}-stack-${stackIndex}`] = new Animated.Value(0);
          });
        }
      });

      setStackedAnimations(newAnimations);
      hasAnimatedRef.current = true;

      setTimeout(() => {
        const totalAnimations = displayData.map((_, index) => {
          return Animated.timing(newAnimations[`bar-${index}`], {
            toValue: 1,
            duration: 800,
            easing: Easing.out(Easing.cubic),
            useNativeDriver: false
          });
        });

        const stackAnimations = displayData.flatMap((item, index) => {
          if (!item.stacks) return [];
          return item.stacks.map((_, stackIndex) => {
            return Animated.timing(
              newAnimations[`bar-${index}-stack-${stackIndex}`],
              {
                toValue: 1,
                duration: 800,
                easing: Easing.out(Easing.cubic),
                useNativeDriver: false
              }
            );
          });
        });

        const allAnimations = [...totalAnimations, ...stackAnimations];
        Animated.stagger(40, allAnimations).start();
      }, 300);
    } else {
      hasAnimatedRef.current = false;
      setStackedAnimations({});
    }
  }, [displayData, selectedPacker]);

  useEffect(() => {
    if (!displayData.length) {
      setShouldScroll(false);
      return;
    }

    const dimensions = calculateDailyChartDimensions(
      displayData,
      containerDimensions,
      maxBarsToShow,
      barGapRatio,
      yAxisWidth
    );

    setBarWidth(dimensions.barWidth);
    setSpacing(dimensions.spacing);
    setChartWidth(dimensions.chartWidth);
    setShouldScroll(dimensions.shouldScroll);

    const optimalTime = calculateOptimalDisplayTime(
      displayData,
      'itemsPerHour',
      maxBarsToShow,
      dimensions.barWidth + dimensions.spacing
    );

    if (
      updateAutoTimeRef.current &&
      typeof updateAutoTimeRef.current === 'function' &&
      optimalTime !== lastAutoTimeRef.current
    ) {
      lastAutoTimeRef.current = optimalTime;
      updateAutoTimeRef.current(optimalTime);
    }
  }, [displayData, containerDimensions]);

  const handleSelectPacker = id => {
    setSelectedPacker(id);
  };

  const chartContainerHeight = containerDimensions.height * 0.8;
  const adjustedChartHeight = chartContainerHeight - 10;
  const labelSpaceHeight = 60;

  const renderFilters = () => {
    if (!packerOptions.length) {
      return null;
    }

    const allOption = {id: 'all', label: 'All Packers'};

    return (
      <View style={styles.filtersContainer}>
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          contentContainerStyle={styles.filtersScroll}>
          {[allOption, ...packerOptions].map(option => {
            const isActive = selectedPacker === option.id;
            return (
              <TouchableOpacity
                key={option.id}
                onPress={() => handleSelectPacker(option.id)}
                style={[
                  styles.filterChip,
                  isActive && styles.filterChipActive
                ]}>
                <Text
                  style={[
                    styles.filterChipText,
                    isActive && styles.filterChipTextActive
                  ]}>
                  {option.label}
                </Text>
              </TouchableOpacity>
            );
          })}
        </ScrollView>
      </View>
    );
  };

  const renderContent = () => {
    if (loading) {
      return <ChartLoading />;
    }

    if (!displayData.length) {
      return (
        <View style={styles.mainContent}>
          {renderFilters()}
          <NoDataDisplay />
        </View>
      );
    }

    return (
      <View style={styles.mainContent}>
        {renderFilters()}
        <View style={styles.chartArea}>
          <View style={styles.chartRow}>
            <YAxisLabels
              values={generateYAxisValues(maxValue || 1, segmentsCount)}
              chartHeight={adjustedChartHeight}
              segmentsCount={segmentsCount}
              containerStyle={{height: adjustedChartHeight + labelSpaceHeight}}
            />
            <AutoScrollWrapper
              enabled={shouldScroll && autoScrollEnabled}
              speed={35}
              pauseAtEnds={2500}
              showIndicators={shouldScroll}
              style={[
                styles.chartBackground,
                {height: adjustedChartHeight + labelSpaceHeight}
              ]}>
              <View
                style={[
                  styles.svgContainer,
                  {
                    height: adjustedChartHeight + labelSpaceHeight,
                    width: chartWidth
                  }
                ]}>
                {selectedPacker === 'all' && displayData.length > 0 && displayData[0]?.isStacked ? (
                  <Svg
                    height={adjustedChartHeight + labelSpaceHeight}
                    width={chartWidth}
                    style={{ overflow: 'visible' }}>
                    <Defs>
                      {displayData.flatMap((item, itemIndex) =>
                        (item.stacks || []).map((stack, stackIndex) => (
                          <LinearGradient
                            key={`gradient-${itemIndex}-${stackIndex}`}
                            id={`gradient-${itemIndex}-${stackIndex}`}
                            x1="0"
                            y1="0"
                            x2="0"
                            y2="1">
                            <Stop
                              offset="0"
                              stopColor={stack.gradientStart || stack.color}
                              stopOpacity="1"
                            />
                            <Stop
                              offset="1"
                              stopColor={stack.gradientEnd || stack.color}
                              stopOpacity="1"
                            />
                          </LinearGradient>
                        ))
                      )}
                    </Defs>
                    <ChartBackground width={chartWidth} height={adjustedChartHeight} />
                    <GridLines
                      chartHeight={adjustedChartHeight}
                      chartWidth={chartWidth}
                      segments={segmentsCount}
                    />
                    {displayData.map((item, index) => {
                      let currentY = 0;
                      const barTotal = item.total || item.value || 0;
                      const barHeight = (barTotal / maxValue) * adjustedChartHeight;
                      const barX = index * (barWidth + spacing);
                      const barCenter = barX + barWidth / 2;
                      const stacks = item.stacks || [];

                      return (
                        <React.Fragment key={`bar-${index}`}>
                          {stacks.map((stack, stackIndex) => {
                            const stackPercentage = stack.value / barTotal;
                            const stackHeight = stackPercentage * barHeight;
                            const stackY = currentY;
                            currentY = currentY + stackHeight;

                            const {showName, nameFontSize, valueFontSize} =
                              getNameDisplaySettings(stackHeight);

                            const totalAnimation =
                              stackedAnimations[`bar-${index}`] ||
                              new Animated.Value(0);
                            const stackAnimation =
                              stackedAnimations[`bar-${index}-stack-${stackIndex}`] ||
                              new Animated.Value(0);
                            const animatedStackHeight = Animated.multiply(
                              stackAnimation,
                              Animated.multiply(
                                totalAnimation,
                                new Animated.Value(stackHeight)
                              )
                            );

                            const animatedY = Animated.add(
                              adjustedChartHeight,
                              Animated.multiply(
                                -1,
                                Animated.add(
                                  Animated.multiply(
                                    totalAnimation,
                                    new Animated.Value(stackY)
                                  ),
                                  animatedStackHeight
                                )
                              )
                            );

                            const textOpacity = Animated.multiply(
                              totalAnimation,
                              stackAnimation
                            );

                            return (
                              <G key={`stack-${index}-${stackIndex}`}>
                                <AnimatedRect
                                  x={barX}
                                  y={animatedY}
                                  width={barWidth}
                                  height={animatedStackHeight}
                                  fill={`url(#gradient-${index}-${stackIndex})`}
                                  rx={15}
                                  ry={15}
                                  opacity={0.7}
                                />
                                {valueFontSize > 0 && (
                                  <AnimatedSvgText
                                    x={barCenter}
                                    y={Animated.subtract(
                                      adjustedChartHeight,
                                      Animated.add(
                                        Animated.add(
                                          Animated.multiply(
                                            totalAnimation,
                                            new Animated.Value(stackY)
                                          ),
                                          Animated.multiply(
                                            animatedStackHeight,
                                            new Animated.Value(0.5)
                                          )
                                        ),
                                        new Animated.Value(
                                          showName ? nameFontSize / 2 + 2 : 0
                                        )
                                      )
                                    )}
                                    fill="white"
                                    textAnchor="middle"
                                    fontWeight="bold"
                                    fontFamily="Poppins_600SemiBold"
                                    fontSize={valueFontSize}
                                    opacity={textOpacity}>
                                    {stack.value}
                                  </AnimatedSvgText>
                                )}
                                {showName && (
                                  <AnimatedSvgText
                                    x={barCenter}
                                    y={Animated.subtract(
                                      adjustedChartHeight,
                                      Animated.add(
                                        Animated.add(
                                          Animated.multiply(
                                            totalAnimation,
                                            new Animated.Value(stackY)
                                          ),
                                          Animated.multiply(
                                            animatedStackHeight,
                                            new Animated.Value(0.5)
                                          )
                                        ),
                                        new Animated.Value(
                                          showName ? -(nameFontSize / 2 + 2) - 10 : 0
                                        )
                                      )
                                    )}
                                    fill="white"
                                    textAnchor="middle"
                                    fontWeight="bold"
                                    fontFamily="Poppins_600SemiBold"
                                    fontSize={nameFontSize}
                                    opacity={Animated.multiply(textOpacity, 0.9)}>
                                    {truncateLabel(stack.name)}
                                  </AnimatedSvgText>
                                )}
                              </G>
                            );
                          })}
                          {barTotal > 0 && (
                            <AnimatedSvgText
                              x={barCenter}
                              y={Animated.add(
                                adjustedChartHeight,
                                Animated.multiply(
                                  -1,
                                  Animated.add(
                                    Animated.multiply(
                                      stackedAnimations[`bar-${index}`] || new Animated.Value(0),
                                      new Animated.Value(barHeight)
                                    ),
                                    new Animated.Value(15)
                                  )
                                )
                              )}
                              fill="white"
                              textAnchor="middle"
                              fontFamily="Poppins_600SemiBold"
                              fontSize={26}
                              opacity={stackedAnimations[`bar-${index}`] || new Animated.Value(0)}
                              fontWeight="bold">
                              {barTotal}
                            </AnimatedSvgText>
                          )}
                          <SvgText
                            x={barCenter}
                            y={adjustedChartHeight + 35}
                            fill="white"
                            textAnchor="middle"
                            fontFamily="Poppins_600SemiBold"
                            fontSize={18}>
                            {truncateLabel(item.label)}
                          </SvgText>
                        </React.Fragment>
                      );
                    })}
                  </Svg>
                ) : (
                  <ChartContent
                    chartHeight={adjustedChartHeight}
                    chartWidth={chartWidth}
                    labelSpaceHeight={labelSpaceHeight}
                    segmentsCount={segmentsCount}
                    data={displayData}
                    barWidth={barWidth}
                    spacing={spacing}
                    maxValue={maxValue || 1}
                    animations={animations}
                    GridLines={GridLines}
                    adaptiveValueFont
                  />
                )}
              </View>
            </AutoScrollWrapper>
          </View>
        </View>
      </View>
    );
  };

  return (
    <View style={styles.container} onLayout={onContainerLayout}>
      {renderContent()}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    width: '100%',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#191919'
  },
  mainContent: {
    flex: 1,
    width: '100%',
    height: '100%',
    paddingVertical: 10,
    paddingBottom: 40,
    paddingHorizontal: 5
  },
  chartArea: {
    flex: 1,
    height: '100%'
  },
  chartRow: {
    flexDirection: 'row',
    height: '100%'
  },
  chartBackground: {
    flex: 1,
    backgroundColor: '#191919'
  },
  svgContainer: {
    borderRadius: 3,
    overflow: 'visible'
  },
  filtersContainer: {
    width: '100%',
    marginBottom: 15
  },
  filtersScroll: {
    paddingHorizontal: 10
  },
  filterChip: {
    paddingVertical: 6,
    paddingHorizontal: 14,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: '#3A3F47',
    marginRight: 10
  },
  filterChipActive: {
    backgroundColor: '#FFFFFF'
  },
  filterChipText: {
    color: '#FFFFFF',
    fontSize: 12,
    fontFamily: 'Poppins_600SemiBold'
  },
  filterChipTextActive: {
    color: '#000000'
  }
});

export default ItemsPerHourChart;
